import React from 'react';
import PropTypes from 'prop-types';

import '../styles/availability/availability.css';

import * as Sentry from '@sentry/browser';

import { useTranslation } from 'react-i18next';

import { WeezHeader, RadioLabel, SplitContainer, Card, Button, ButtonDropdown, Icon, ToastContext } from '@weezevent/nacre';

import AvailabilityModel from '../models/availability';
import Contingent from '../models/contingentModel';
import Category from '../models/category';
import Rate from '../models/rateModel';

import InventoryPicker from '../components/contingent/inventoryPicker';
import { SeatMap, ContingentQuota, CategoryQuota } from './placement/map';
import { SeatStyles as SeatSVGStyles } from './placement/seatStyles';
import useSeatedContext from '../context/seatedContext';
import LoadingCard from '../components/loadingCard';
import ExportButton from '../components/export/exportButton';

const SeatStyles = ({ type = 'contingents', contingents, categories, availability }) => {
    if (type === 'contingents') {
        return <SeatSVGStyles items={contingents} query="data-contingent-id" />;
    }

    if (type === 'categories') {
        return <SeatSVGStyles items={categories} query="data-category-id" />;
    }

    return null;
};

export default function Availability({ event, organization, history, zoneId = '' }) {
    const [loading, setLoading] = React.useState(true);

    const [currentAvailability, setAvailability] = React.useState(null);
    const [display, setDisplay] = React.useState('contingents');
    const [{ inventory, inventories }] = useSeatedContext();
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);

    const updateURL = React.useCallback(
        (zone, new_inventory = null) => {
            let inventory_id = (new_inventory || inventory).uuid;
            history.push(`/ticket/O${organization.id}/E${event.id}/seating/availability/inventory/${inventory_id}/zone/${zone.uuid || 'root'}`);
        },
        [organization, event, inventory, history]
    );

    const [contingents, loadingContingents] = Contingent.useApiModel({
        organizationId: organization.id,
        eventId: event.id
    });

    const [categories, loadingCategories] = Category.useApiModel({
        organizationId: organization.id,
        chartId: event.chart
    });

    const [rates, loadingRates] = Rate.useApiModel({
        organizationId: organization.id,
        eventId: event.id
    });

    const availability = React.useCallback(
        (queryParams = {}) => {
            setLoading(true);
            AvailabilityModel.getAll({
                organizationId: organization.id,
                eventId: event.id,
                inventoryId: inventory.uuid,
                zoneId: zoneId === 'root' ? '' : zoneId,
                cache: false,
                query: queryParams
            })
                .then(availability => {
                    setAvailability(availability);
                    setLoading(false);
                })
                .catch(e => {
                    Sentry.configureScope(scope => {
                        scope.setExtra('zone_id', zoneId);
                    });
                    Sentry.captureException(e);
                    toast.error(t('ticket.availability.retrieval_error'));
                });
        },
        [inventory, zoneId]
    );

    // Reload availability on inventory change
    React.useEffect(() => {
        if (!inventory) {
            return;
        }

        availability();
    }, [availability]);

    const onPreviousZone = React.useCallback(() => {
        let oldZone = {
            uuid: currentAvailability.parent || ''
        };
        updateURL(oldZone);
    }, [updateURL, currentAvailability]);

    const onZoneSelected = React.useCallback(
        zone => {
            // This means zone is GA. I'm actually impressed Throne does not add a specific method for this
            if (zone.categories.length === 1 && zone.categories[0].contingents[0] && !zone.categories[0].contingents[0]?.seated) {
                return;
            }
            updateURL(zone);
        },
        [updateURL]
    );

    const changeInventory = React.useCallback(
        new_inventory => {
            if (new_inventory.uuid === inventory.uuid) {
                return;
            }

            updateURL({ uuid: zoneId }, new_inventory);
        },
        [updateURL, inventory, zoneId]
    );

    const loadings = React.useMemo(() => {
        return loading || loadingRates || loadingContingents || loadingCategories;
    }, [loading, loadingRates, loadingCategories, loadingContingents]);

    const exportPath = React.useMemo(() => {
        return `/ticket/organizations/${organization.pk}/events/${event.id}/inventories/${inventory.uuid}/export/`;
    }, [organization, event, inventory]);

    return (
        <div className="placement">
            <WeezHeader
                title={t('main_menu_configure_availability')}
                rightComponent={[
                    <ButtonDropdown
                        key="dropdown"
                        right
                        renderWhenClosed
                        trigger={<Button inverted primary icon={<Icon name="chevron-bottom" />} label={t('ticket.common.cta.actions')} />}
                        items={[
                            <ExportButton
                                key="export_seats"
                                path={exportPath}
                                organization={organization}
                                eventIds={[event.id]}
                                choiceProcess="seat_inventories"
                                showButton={false}
                                buttonTrans="ticket.availability.export_seats"
                                onError={() => toast.error(t('ticket.export.download_state_error'))}
                            />,
                            <ExportButton
                                key="export_reservations"
                                path={exportPath}
                                organization={organization}
                                eventIds={[event.id]}
                                choiceProcess="bookings"
                                showButton={false}
                                buttonTrans="ticket.availability.export_reservations"
                                onError={() => toast.error(t('ticket.export.download_state_error'))}
                            />
                        ]}
                    />
                ]}
            />
            <SeatStyles type={display} categories={categories} contingents={contingents} />
            <SplitContainer
                additional={
                    <Card className={'additional'}>
                        <Card.Header>
                            <Card.Title title={display === 'contingents' ? t('ticket.availability.titleListContingents') : t('ticket.availability.titleListCategories')} />
                        </Card.Header>
                        <Card.Content>
                            {display === 'contingents' && (
                                <ContingentQuota availability={currentAvailability} contingents={contingents} event={event} organization={organization} inventory={inventory} />
                            )}
                            {display === 'categories' && (
                                <CategoryQuota availability={currentAvailability} categories={categories} event={event} organization={organization} inventory={inventory} />
                            )}
                        </Card.Content>
                    </Card>
                }
            >
                <Card>
                    <Card.Header>
                        <Card.Title title={display === 'contingents' ? t('ticket.availability.titleContingents') : t('ticket.availability.titleCategories')} />
                    </Card.Header>
                    <Card.Content>
                        <div>
                            <RadioLabel
                                className="radio-label-custom"
                                name="availability_display"
                                value="contingents"
                                checked={display === 'contingents'}
                                inline
                                bordered
                                onChange={(event, { checked }) => {
                                    if (checked) {
                                        setDisplay('contingents');
                                    }
                                }}
                            >
                                {t('ticket.availability.show_contingents')}
                            </RadioLabel>
                            <RadioLabel
                                className="radio-label-custom"
                                name="availability_display"
                                value="categories"
                                checked={display === 'categories'}
                                inline
                                bordered
                                onChange={(event, { checked }) => {
                                    if (checked) {
                                        setDisplay('categories');
                                    }
                                }}
                            >
                                {t('ticket.availability.show_categories')}
                            </RadioLabel>
                        </div>
                        <LoadingCard loading={loadings}>
                            <div className={'seat-map-card'}>
                                <InventoryPicker currentInventory={inventory} inventories={inventories} onChange={changeInventory} />
                                <div className="throne-container">
                                    <SeatMap
                                        availability={currentAvailability}
                                        contingents={contingents}
                                        rates={rates}
                                        backButton={Boolean(currentAvailability?.parent)}
                                        sync={() => availability({ force_cache_refresh: 1 })}
                                        onBack={onPreviousZone}
                                        onZoneSelected={onZoneSelected}
                                    />
                                </div>
                            </div>
                        </LoadingCard>
                    </Card.Content>
                </Card>
            </SplitContainer>
        </div>
    );
}

Availability.propTypes = {
    event: PropTypes.object.isRequired,
    organization: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    zoneId: PropTypes.string.isRequired
};
